import React, { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Spin } from 'antd';
import { useQueryClient } from 'react-query';
import cn from 'classnames';
import useForm from '../../hooks/useForm';
import createMeterReadings from '../../requests/createMeterReadings';
import queries from '../../queries/queries';
import styles from './SendMeters.module.scss';

const readingValidator = (value, prevValue) => {
  const numRegExp = /^[0-9]*[.,]?[0-9]+$/;

  const val = value?.trim();

  if (!val) {
    return 'Укажите значение';
  } else if (!numRegExp.test(value)) {
    return 'Поле принимает только цифры и точку/запятую. Введите целое число без запятой/точки или дробное число';
  } else if (Number(val) < prevValue) {
    return 'Значение не может быть меньше последнего показания';
  }
};

const SendMeters = ({ meter, meterReading }) => {
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const [inputsState, setInputsState] = useState({});
  const [validators, setValidators] = useState(null);

  const setInitialInputsState = useCallback(() => {
    const data = (meter.tariffs || []).reduce(
      (acc, tariff) => {
        const { inputState, validators } = acc;
        inputState[tariff.shortName] = {
          value: Boolean(meterReading?.length)
            ? `${meterReading[0].value}`
            : '',
          error: '',
          tariff: { id: tariff.id },
        };

        if (meter.rentalHouseTariff) {
          inputState[tariff.shortName].tariffValue =
            meter.rentalHouseTariff.value;
        }

        validators[tariff.shortName] = (value) =>
          readingValidator(
            value,
            Boolean(meterReading?.length) ? meterReading[0].value : 0
          );

        return acc;
      },
      { inputState: {}, validators: {} }
    );

    setInputsState(data.inputState);
    setValidators(data.validators);
  }, [meter, meterReading]);

  useEffect(() => {
    setInitialInputsState();
  }, [setInitialInputsState]);

  const handleSubmit = useCallback(
    async (data, reset) => {
      const meterReadings = Object.keys(data).map((key) => {
        const { error, prevValue, ...dataWithoutError } = data[key];
        return dataWithoutError;
      });

      const values = { meterReadings };
      setIsLoading(true);
      const response = await createMeterReadings(meter.id, values);

      if (response) {
        queryClient.invalidateQueries(queries.meterReading);
        queryClient.invalidateQueries(queries.facilityMeters);
      }
      setIsLoading(false);
      reset();
    },
    [queryClient, meter.id]
  );

  const { state, handleOnChange, handleOnSubmit, disable } = useForm(
    inputsState,
    validators,
    handleSubmit
  );

  return (
    <form onSubmit={handleOnSubmit} className={styles.sendMetersContainer}>
      <div
        className={cn(styles.inputMeters, {
          [styles.empty]: !meter.tariffs || !meter.tariffs.length,
        })}
      >
        {!meter.tariffs || !meter.tariffs.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          meter.tariffs.map((tariff) => {
            const tariffName = tariff.name.includes(' ')
              ? tariff.name.split(' ')[1]
              : tariff.name;

            const name =
              meter.tariffs.length > 1
                ? `${tariffName[0].toUpperCase()}${tariffName.slice(1)}`
                : 'Общее';

            const shortName = tariff.shortName;

            const early = meterReading?.find(
              (item) => item.tariff.name === tariff.name
            );

            return (
              <div className={styles.flex} key={tariff.id}>
                <div
                  className={cn(styles.inputBlock, {
                    [styles.inputBlockError]: state[shortName]?.error,
                  })}
                >
                  <div className={styles.partOfDay}>{name}</div>
                  <input
                    name={shortName}
                    placeholder="000000"
                    className={styles.inputValue}
                    onChange={handleOnChange}
                    value={state[shortName]?.value || ''}
                    disabled={isLoading}
                  />
                </div>
                <div>
                  Ранее: {meterReading.length && early ? early.value : 0}
                </div>

                {state[shortName]?.error && (
                  <p className={styles.inputError}>{state[shortName].error}</p>
                )}
              </div>
            );
          })
        )}
      </div>
      <Spin spinning={isLoading}>
        <Button
          htmlType="submit"
          disabled={disable}
          className={styles.sendMetersButton}
          type={'primary'}
        >
          Отправить показания
        </Button>
      </Spin>
    </form>
  );
};

export default SendMeters;
